import React, { useMemo } from 'react';
import { T, panelStyle, GradientTitle, SparkLine, StatusDot } from './NOCShared';
import { formatTime } from '../../utils/formatTime';

const MITRE_TACTICS = [
  { id: 'TA0001', name: 'Initial Access', level: 3 },
  { id: 'TA0002', name: 'Execution', level: 2 },
  { id: 'TA0003', name: 'Persistence', level: 3 },
  { id: 'TA0004', name: 'Privilege Esc', level: 2 },
  { id: 'TA0005', name: 'Defense Evasion', level: 3 },
  { id: 'TA0006', name: 'Credential Access', level: 1 },
  { id: 'TA0007', name: 'Discovery', level: 2 },
  { id: 'TA0008', name: 'Lateral Movement', level: 3 },
  { id: 'TA0009', name: 'Collection', level: 1 },
  { id: 'TA0010', name: 'Exfiltration', level: 3 },
  { id: 'TA0011', name: 'C2', level: 2 },
  { id: 'TA0040', name: 'Impact', level: 1 },
];

function ThreatHeatmap({ securityEvents, incidents }) {
  const zones = useMemo(() => {
    const z = [];
    for (let i = 0; i < 15; i++) {
      const threatLevel = Math.random() * (securityEvents.length > 3 ? 0.6 : 0.2) + (incidents.filter(inc => inc.severity === 'critical').length > 0 ? 0.2 : 0);
      z.push({ id: i, level: Math.min(threatLevel, 1) });
    }
    return z;
  }, [securityEvents.length, incidents]);

  const getColor = (level) => {
    if (level > 0.5) return `${T.pink}cc`;
    if (level > 0.2) return `${T.purple}99`;
    return `${T.success}44`;
  };

  return (
    <div className="flex items-center gap-0.5" style={{ height: '60px' }}>
      {zones.map((z) => (
        <div key={z.id} className="flex-1 transition-all duration-700"
          style={{
            background: getColor(z.level),
            height: `${20 + z.level * 80}%`,
            boxShadow: z.level > 0.4 ? `0 0 8px ${getColor(z.level)}` : 'none',
            borderRadius: 2,
          }} />
      ))}
    </div>
  );
}

function MITREMatrix() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {MITRE_TACTICS.map((tactic) => (
        <div key={tactic.id} className="flex items-center gap-1">
          <span className="text-[7px] w-8 truncate" style={{ color: T.textDim }}>{tactic.id}</span>
          <div className="flex-1 flex items-center gap-px">
            {[1, 2, 3].map((lvl) => (
              <div key={lvl} className="flex-1 h-2 transition-all duration-500"
                style={{
                  background: lvl <= tactic.level
                    ? `linear-gradient(90deg, ${T.pink}, ${T.purple})`
                    : 'rgba(255,255,255,0.04)',
                  boxShadow: lvl <= tactic.level ? `0 0 3px ${T.purple}40` : 'none',
                  borderRadius: 2,
                }} />
            ))}
          </div>
          <span className="text-[7px] w-16 truncate text-right" style={{ color: T.textDim }}>{tactic.name}</span>
        </div>
      ))}
    </div>
  );
}

export default function NOCRightPanel({
  securityEvents, securityEventsSummary, incidents,
  aiStats, activityFeed, dbSizes, chatopsStats,
}) {
  const criticalIncidents = useMemo(() =>
    incidents.filter(i => i.severity === 'critical' && i.status !== 'resolved'), [incidents]);
  const highIncidents = useMemo(() =>
    incidents.filter(i => i.severity === 'high' && i.status !== 'resolved'), [incidents]);
  const otherIncidents = useMemo(() =>
    incidents.filter(i => i.severity !== 'critical' && i.severity !== 'high' && i.status !== 'resolved').slice(0, 3), [incidents]);

  return (
    <div className="flex flex-col gap-1.5 overflow-y-auto h-full" style={{ scrollbarWidth: 'thin' }}>
      {/* Panel 1: Threat Intelligence */}
      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="Threat Intelligence" />
        <div style={{ marginBottom: 8 }}>
          <ThreatHeatmap securityEvents={securityEvents} incidents={incidents} />
        </div>
        <div className="grid grid-cols-3 gap-1 text-center">
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.danger }}>{securityEventsSummary.total_today || 0}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>Events</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.warning }}>{securityEventsSummary.blocked || 0}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>Blocked</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.purple }}>{criticalIncidents.length + highIncidents.length}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>Threats</div>
          </div>
        </div>
        {securityEvents.length > 0 && (
          <div className="max-h-16 overflow-y-auto" style={{ scrollbarWidth: 'thin', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {securityEvents.slice(0, 4).map((ev, idx) => (
              <div key={idx} className="flex items-center gap-1" style={{ background: 'rgba(239,68,68,0.06)', padding: '2px 4px', borderRadius: 4 }}>
                <StatusDot status={ev.severity === 'high' || ev.severity === 'critical' ? 'critical' : 'running'} />
                <span className="text-[8px] truncate flex-1" style={{ color: T.text }}>{ev.description || ev.event_type || 'Event'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Panel 2: Active Security Incidents */}
      <div style={{ ...panelStyle, padding: 12 }} className="flex-1 min-h-0 flex flex-col">
        <GradientTitle text="Security Incidents" />
        <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
          <span className="text-[8px]" style={{ background: 'rgba(239,68,68,0.15)', color: T.danger, padding: '2px 6px', borderRadius: 999 }}>
            {criticalIncidents.length} critical
          </span>
          <span className="text-[8px]" style={{ background: 'rgba(245,158,11,0.15)', color: T.warning, padding: '2px 6px', borderRadius: 999 }}>
            {highIncidents.length} high
          </span>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[...criticalIncidents, ...highIncidents, ...otherIncidents].slice(0, 10).map((inc, idx) => (
            <div key={inc.id || idx} style={{
              background: inc.severity === 'critical' ? 'rgba(239,68,68,0.08)' : inc.severity === 'high' ? 'rgba(245,158,11,0.06)' : 'rgba(255,255,255,0.02)',
              borderLeft: `2px solid ${inc.severity === 'critical' ? T.danger : inc.severity === 'high' ? T.warning : T.textDim}`,
              padding: 6,
              borderRadius: 6,
            }}>
              <div className="flex items-start gap-1.5">
                <StatusDot status={inc.severity} />
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] truncate" style={{ color: T.textBright }}>{inc.title || inc.description || 'Incident'}</p>
                  <div className="flex gap-2 text-[7px]" style={{ color: T.textDim, marginTop: 2 }}>
                    <span>{inc.service || inc.category || ''}</span>
                    {inc.created_at && <span>{formatTime(inc.created_at, { timeOnly: true })}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {criticalIncidents.length === 0 && highIncidents.length === 0 && otherIncidents.length === 0 && (
            <div className="text-center text-[9px]" style={{ color: T.success, padding: '16px 0' }}>All systems secure — no active threats</div>
          )}
        </div>
      </div>

      {/* Panel 3: MITRE ATT&CK + AI Stats */}
      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="Defensive Posture" />
        <div className="grid grid-cols-3 gap-1 text-center" style={{ marginBottom: 8 }}>
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.purple }}>{aiStats?.total_analyses || 0}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>AI Analyses</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.pink }}>{aiStats?.autonomous_fixes || 0}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>Auto-Fixes</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
            <div className="font-mono text-xs font-bold" style={{ color: T.success }}>{incidents.filter(i => i.status === 'resolved').length}</div>
            <div className="text-[7px] uppercase tracking-wider" style={{ color: T.textDim }}>Resolved</div>
          </div>
        </div>
        <div className="max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          <MITREMatrix />
        </div>
      </div>
    </div>
  );
}
